import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../../constants';
import { QueueLengthPerformanceItem } from '../../../../../../hooks';
import { Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../../../translations';
import { getFormattedTimeUnit } from '../../../../../../utils';
import { TillAggregateData } from '../gridView/GridView';
import { getTillAveragePercent } from '../util';
import { TillType } from './MobileGrid';
import { FlexContainer, TillWrapper } from './MobileGrid.styles';

export type TillBreakdownProps = {
  performanceItems: QueueLengthPerformanceItem[];
  aggregateData: TillAggregateData[];
  interval: StorePerformanceInterval;
  tillType: TillType;
  expandedTills: Record<string, boolean>;
  toggleTill: (tillId: string) => void;
};

const { queueLength: queueLengthTranslations } = homeTranslations;

export const TillBreakdown: FC<TillBreakdownProps> = ({
  aggregateData,
  tillType,
  interval,
  expandedTills,
  performanceItems,
  toggleTill,
}) => {
  const { t } = useTranslation();

  return (
    <FlexContainer hasBorder borderRadius={[4]} data-testid="till-type-content-container">
      {aggregateData.map((tillAggregateData) => {
        const { tillId, values } = tillAggregateData;

        return (
          <TillWrapper
            justifyContent="between"
            padding={[3, 4]}
            key={`${tillType}-${tillId}`}
            isOpen={expandedTills[tillId]}
            data-testid="till-container"
          >
            <RowFlexContainer
              justifyContent="between"
              alignItems="center"
              cursor="pointer"
              data-testid="till-breakdown-header"
              onClick={() => toggleTill(tillId)}
            >
              <Typography type={expandedTills[tillId] ? 'body3' : 'body2'}>
                {t(queueLengthTranslations.tillId.key, queueLengthTranslations.tillId.defaultValue)}: {tillId}
              </Typography>
              <Typography type={expandedTills[tillId] ? 'body3' : 'body2'}>
                {getTillAveragePercent(tillAggregateData).toFixed(0)}%
              </Typography>
            </RowFlexContainer>
            {expandedTills[tillId] && (
              <ColumnFlexContainer
                gap={[1]}
                margin={[2, 0, 0]}
                padding={[2, 0, 0]}
                maxHeight={[25]}
                overflow="scroll"
                data-testid="till-breakdown-container"
              >
                {performanceItems.map((performanceItem) => {
                  const { timeUnit } = performanceItem;

                  const timeUnitValues = values[timeUnit];

                  if (!timeUnitValues) {
                    return (
                      <RowFlexContainer key={timeUnit} justifyContent="between">
                        <Typography>{timeUnit}</Typography>
                        <Typography>{'-'}</Typography>
                      </RowFlexContainer>
                    );
                  }

                  const percentValue =
                    (timeUnitValues.transactions === 0
                      ? 0
                      : timeUnitValues.queueCount / timeUnitValues.transactions) * 100;

                  return (
                    <RowFlexContainer key={timeUnit} justifyContent="between">
                      <Typography>{getFormattedTimeUnit(timeUnit, interval)}</Typography>
                      <Typography>{percentValue.toFixed(0)}%</Typography>
                    </RowFlexContainer>
                  );
                })}
              </ColumnFlexContainer>
            )}
          </TillWrapper>
        );
      })}
    </FlexContainer>
  );
};
